import React, { createContext, useContext, useState, useEffect } from 'react'

interface AlertContextType {
  showAlert: (message: string, title?: string, type?: 'info' | 'success' | 'error' | 'warning') => void
}

const AlertContext = createContext<AlertContextType | undefined>(undefined)

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    message: string
    title: string
    type: 'info' | 'success' | 'error' | 'warning'
  }>({
    isOpen: false,
    message: '',
    title: 'การแจ้งเตือน',
    type: 'info'
  })

  const showAlert = (message: string, title: string = 'การแจ้งเตือน', type: 'info' | 'success' | 'error' | 'warning' = 'info') => {
    let finalType = type
    let finalTitle = title

    // Auto-detect type and title based on emojis or keywords in message
    if (message.includes('⚠️') || message.includes('สิทธิ์') || message.includes('คำเตือน') || message.includes('กรุณาเลือก')) {
      finalType = 'warning'
      if (title === 'การแจ้งเตือน') finalTitle = '⚠️ คำเตือน'
    } else if (message.includes('❌') || message.includes('ล้มเหลว') || message.includes('ผิดพลาด')) {
      finalType = 'error'
      if (title === 'การแจ้งเตือน') finalTitle = '❌ เกิดข้อผิดพลาด'
    } else if (message.includes('🔌') || message.includes('สำเร็จ') || message.includes('💾') || message.includes('📧') || message.includes('🎉') || message.includes('เรียบร้อย')) {
      finalType = 'success'
      if (title === 'การแจ้งเตือน') finalTitle = '🎉 ดำเนินการสำเร็จ'
    } else if (message.includes('⌛') || message.includes('กำลัง') || message.includes('รอสักครู่')) {
      finalType = 'info'
      if (title === 'การแจ้งเตือน') finalTitle = '⌛ กรุณารอสักครู่'
    }

    setModalState({
      isOpen: true,
      message,
      title: finalTitle,
      type: finalType
    })
  }

  // Override window.alert globally on mount so any alert() call uses this custom dialog
  useEffect(() => {
    const originalAlert = window.alert
    window.alert = (message: any) => {
      showAlert(String(message))
    }
    return () => {
      window.alert = originalAlert
    }
  }, [])

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      {modalState.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 17, 26, 0.75)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div className="glassmorphism" style={{
            padding: '28px',
            width: '100%',
            maxWidth: '440px',
            position: 'relative',
            border: `1px solid ${
              modalState.type === 'error' ? 'rgba(239, 68, 68, 0.25)' :
              modalState.type === 'warning' ? 'rgba(245, 158, 11, 0.25)' :
              modalState.type === 'success' ? 'rgba(0, 184, 148, 0.25)' :
              'rgba(99, 102, 241, 0.25)'
            }`,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)',
            animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            textAlign: 'center'
          }}>
            {/* Header / Title */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '800',
                color: 'var(--text-primary)',
                margin: 0
              }}>
                {modalState.title}
              </h3>
              <button 
                onClick={() => setModalState(prev => ({ ...prev, isOpen: false }))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  lineHeight: 1,
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
              >
                ✕
              </button>
            </div>

            {/* Message Body */}
            <div style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              lineHeight: '1.6',
              marginBottom: '24px',
              whiteSpace: 'pre-line',
              textAlign: 'left'
            }}>
              {modalState.message}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setModalState(prev => ({ ...prev, isOpen: false }))}
                className="btn btn-primary"
                style={{
                  padding: '8px 24px',
                  fontWeight: '700',
                  borderRadius: '8px',
                  backgroundColor: 
                    modalState.type === 'error' ? '#ef4444' :
                    modalState.type === 'warning' ? '#f59e0b' :
                    modalState.type === 'success' ? '#00b894' :
                    'var(--primary)'
                }}
              >
                ตกลง
              </button>
            </div>
          </div>
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes slideUp { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          `}</style>
        </div>
      )}
    </AlertContext.Provider>
  )
}

export const useAlert = () => {
  const context = useContext(AlertContext)
  if (context === undefined) {
    throw new Error('useAlert must be used within an AlertProvider')
  }
  return context
}
